/**
 * AAAprinters - Enhanced User Profile Page
 * Complete profile management with orders, designs, and settings
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { useLanguage } from "@/contexts/LanguageContext";
import { useUserOrders } from "@/hooks/useUserOrders";
import { useUserDesigns } from "@/hooks/useUserDesigns";
import { useUserStats } from "@/hooks/useUserStats";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { ProfileHeader } from "@/components/profile/ProfileHeader";
import { OrdersList } from "@/components/profile/OrdersList";
import { DesignsGallery } from "@/components/profile/DesignsGallery";
import { AccountSettings } from "@/components/profile/AccountSettings";
import { WishlistPreview } from "@/components/profile/WishlistPreview";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  User,
  Package,
  Palette,
  Heart,
  Settings,
  Loader2,
  ShoppingBag,
  MapPin,
  TrendingUp,
  Calendar,
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { getCurrentUserProfile } from "@/lib/supabase-auth";
import { formatBHD } from "@/lib/utils";

const Profile = () => {
  const { user, signOut, loading: authLoading } = useAuth();
  const { isRTL } = useLanguage();
  const navigate = useNavigate();

  // Data hooks
  const { orders, loading: ordersLoading } = useUserOrders();
  const { designs, loading: designsLoading, deleteDesign, toggleFavorite } = useUserDesigns();
  const { stats, loading: statsLoading, refreshStats } = useUserStats();

  // Local state
  const [profile, setProfile] = useState<any>(null);
  const [addresses, setAddresses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Redirect if not logged in
  useEffect(() => {
    if (!authLoading && !user) {
      toast.error("Please login to view your profile");
      navigate("/");
    }
  }, [user, authLoading, navigate]);

  // Load profile data
  useEffect(() => {
    if (user) {
      loadProfileData();
    }
  }, [user]);

  const loadProfileData = async () => {
    if (!user) return;

    setLoading(true);

    try {
      // Load profile
      const profileData = await getCurrentUserProfile();
      setProfile(profileData);

      // Load addresses
      const { data: addressesData } = await supabase
        .from("addresses")
        .select("*")
        .eq("user_id", user.id)
        .order("is_default", { ascending: false });

      setAddresses(addressesData || []);
    } catch (error) {
      console.error("Error loading profile:", error);
      toast.error("Failed to load profile data");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      toast.success("Logged out successfully");
      navigate("/");
    } catch (error) {
      toast.error("Failed to logout");
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading your profile...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />

      <main className="flex-1 bg-gradient-to-br from-background via-background to-muted/20">
        {/* Profile Header */}
        <ProfileHeader
          user={user}
          profile={profile}
          memberSince={stats?.memberSince}
          onLogout={handleLogout}
        />

        {/* Profile Content */}
        <div className="container mx-auto px-4 py-8">
          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="grid w-full grid-cols-5 lg:w-auto lg:inline-grid">
              <TabsTrigger value="overview">
                <User className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Overview</span>
              </TabsTrigger>
              <TabsTrigger value="orders">
                <Package className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Orders</span>
              </TabsTrigger>
              <TabsTrigger value="designs">
                <Palette className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Designs</span>
              </TabsTrigger>
              <TabsTrigger value="wishlist">
                <Heart className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Wishlist</span>
              </TabsTrigger>
              <TabsTrigger value="settings">
                <Settings className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Settings</span>
              </TabsTrigger>
            </TabsList>

            {/* OVERVIEW TAB */}
            <TabsContent value="overview" className="space-y-6">
              {/* Stats Cards */}
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Total Orders</p>
                        <p className="text-3xl font-bold text-primary">
                          {statsLoading ? "..." : stats?.totalOrders || 0}
                        </p>
                      </div>
                      <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
                        <ShoppingBag className="h-6 w-6 text-blue-600 dark:text-blue-200" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Total Spent</p>
                        <p className="text-3xl font-bold text-primary">
                          {statsLoading ? "..." : formatBHD(stats?.totalSpent || 0)}
                        </p>
                      </div>
                      <div className="p-3 bg-green-100 dark:bg-green-900 rounded-lg">
                        <TrendingUp className="h-6 w-6 text-green-600 dark:text-green-200" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Saved Designs</p>
                        <p className="text-3xl font-bold text-primary">
                          {designsLoading ? "..." : designs.length}
                        </p>
                      </div>
                      <div className="p-3 bg-gradient-to-r from-brand-cyan to-brand-magenta rounded-lg">
                        <Palette className="h-6 w-6 text-white" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Active Orders</p>
                        <p className="text-3xl font-bold text-primary">
                          {statsLoading ? "..." : stats?.activeOrders || 0}
                        </p>
                      </div>
                      <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-lg">
                        <Package className="h-6 w-6 text-purple-600 dark:text-purple-200" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Activity Summary */}
              <div className="grid gap-6 md:grid-cols-2">
                {/* Recent Activity */}
                <Card>
                  <CardHeader>
                    <CardTitle>Account Activity</CardTitle>
                    <CardDescription>Your recent shopping activity</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium">Completed Orders</p>
                        <p className="text-xs text-muted-foreground">Successfully delivered</p>
                      </div>
                      <p className="text-2xl font-bold">{stats?.completedOrders || 0}</p>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium">Average Order Value</p>
                        <p className="text-xs text-muted-foreground">Per order</p>
                      </div>
                      <p className="text-2xl font-bold">
                        {formatBHD(stats?.averageOrderValue || 0)}
                      </p>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium">Favorite Designs</p>
                        <p className="text-xs text-muted-foreground">Marked as favorites</p>
                      </div>
                      <p className="text-2xl font-bold">{stats?.favoriteDesigns || 0}</p>
                    </div>

                    {stats?.lastOrderDate && (
                      <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium">Last Order</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(stats.lastOrderDate).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Quick Actions */}
                <Card>
                  <CardHeader>
                    <CardTitle>Quick Actions</CardTitle>
                    <CardDescription>Manage your account</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => navigate("/shop")}
                    >
                      <ShoppingBag className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                      Continue Shopping
                    </Button>

                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => navigate("/design-studio")}
                    >
                      <Palette className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                      Open Design Studio
                    </Button>

                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => navigate("/wishlist")}
                    >
                      <Heart className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                      View Wishlist
                    </Button>

                    <Button
                      variant="outline"
                      className="w-full justify-start"
                    >
                      <MapPin className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                      Manage Addresses ({addresses.length})
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* ORDERS TAB */}
            <TabsContent value="orders">
              <OrdersList orders={orders} loading={ordersLoading} />
            </TabsContent>

            {/* DESIGNS TAB */}
            <TabsContent value="designs">
              <DesignsGallery
                designs={designs}
                loading={designsLoading}
                onDelete={deleteDesign}
                onToggleFavorite={toggleFavorite}
              />
            </TabsContent>

            {/* WISHLIST TAB */}
            <TabsContent value="wishlist">
              <Card>
                <CardHeader>
                  <CardTitle>My Wishlist</CardTitle>
                  <CardDescription>Products you love</CardDescription>
                </CardHeader>
                <CardContent>
                  {user && <WishlistPreview userId={user.id} onRemove={refreshStats} />}
                </CardContent>
              </Card>
            </TabsContent>

            {/* SETTINGS TAB */}
            <TabsContent value="settings">
              <AccountSettings
                user={user}
                profile={profile}
                onProfileUpdate={loadProfileData}
              />
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Profile;
